import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

interface FileRow {
  accountingQuarter: number;
  e53FileName: string;
  e53FileStatus: string;
  e55FileName: string;
  e55FileStatus: string;
}

interface FileNamesDisplayProps {
  fileRows: FileRow[];
  direction: 'inbound' | 'outbound';
}

function FileNamesDisplay({ fileRows, direction }: FileNamesDisplayProps) {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getDirectionColor = () => {
    return direction === 'inbound' ? '#E8F5E8' : '#FFEBEE'; // Light green for inbound, light red for outbound
  };

  const getDirectionBorderColor = () => {
    return direction === 'inbound' ? '#4CAF50' : '#F44336'; // Green border for inbound, red border for outbound
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="file-names-display">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#063142',
          mb: 2,
        }}
        data-testid="file-names-display-title"
      >
        {direction === 'inbound' ? 'Inbound' : 'Outbound'} File Information
      </Typography>
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none', 
          my: 2,
          backgroundColor: getDirectionColor()
        }}
        data-testid="file-names-display-table-container"
      >
        <Table size="small" data-testid="file-names-display-table">
          <TableHead data-testid="file-names-display-table-head">
            <TableRow sx={{ backgroundColor: direction === 'inbound' ? '#C8E6C9' : '#FFCDD2' }} data-testid="file-names-display-table-header-row">
              <TableCell sx={{ fontWeight: 600, color: '#063142', fontSize: '14px' }}>
                Quarter
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142', fontSize: '14px' }}>
                e53 File
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142', fontSize: '14px' }}>
                e53 Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142', fontSize: '14px' }}>
                e55 File
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#063142', fontSize: '14px' }}>
                e55 Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="file-names-display-table-body">
            {fileRows.map((row, index) => (
              <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F8F9FA' } }} data-testid={`file-names-display-table-row-${index}`}>
                <TableCell sx={{ fontSize: '14px', color: '#063142' }}>
                  Q{row.accountingQuarter}
                </TableCell>
                <TableCell sx={{ fontSize: '14px', color: '#063142', fontFamily: 'monospace' }}>
                  {row.e53FileName || 'N/A'}
                </TableCell>
                <TableCell>
                  {row.e53FileStatus && row.e53FileStatus.trim() !== '' && (
                    <Chip
                      label={getStatusLabel(row.e53FileStatus)}
                      color={getStatusColor(row.e53FileStatus)}
                      size="small"
                      sx={{ fontSize: '12px' }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '14px', color: '#063142', fontFamily: 'monospace' }}>
                  {row.e55FileName || 'N/A'}
                </TableCell>
                <TableCell>
                  {row.e55FileStatus && row.e55FileStatus.trim() !== '' && (
                    <Chip
                      label={getStatusLabel(row.e55FileStatus)}
                      color={getStatusColor(row.e55FileStatus)}
                      size="small"
                      sx={{ fontSize: '12px' }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default FileNamesDisplay;
